import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import AdminLayout from '../../components/admin/AdminLayout';
import ConsultantDocumentManager from '../../components/admin/ConsultantDocumentManager';
import WorkdayCommunityManager from '../../components/admin/WorkdayCommunityManager';
import {
  MessageSquare,
  Image,
  ShieldAlert,
  RotateCcw,
  Server,
  ShieldCheck,
  CreditCard,
  Building2,
  UserCog,
  Users,
  Briefcase,
  ClipboardList,
  BarChart3,
  UserCheck,
  ArrowRight,
  Activity,
  AlertTriangle,
} from 'lucide-react';

interface QuickStat {
  label: string;
  value: number | string;
  color: string;
}

interface NavCard {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  color: string;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    contacts: 0,
    consultants: 0,
    clients: 0,
    openIncidents: 0,
  });
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    checkAuth();
    fetchStats();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) navigate('/admin/login');
  };

  const fetchStats = async () => {
    setLoadingStats(true);
    const [contactsRes, consultantsRes, clientsRes, incidentsRes] = await Promise.all([
      supabase.from('contacts').select('id', { count: 'exact', head: true }),
      supabase.from('consultant_profiles').select('id', { count: 'exact', head: true }).is('deleted_at', null),
      supabase.from('workday_client_profiles').select('id', { count: 'exact', head: true }).is('deleted_at', null),
      supabase.from('security_incidents').select('id', { count: 'exact', head: true }).in('status', ['open', 'investigating']),
    ]);
    setStats({
      contacts: contactsRes.count ?? 0,
      consultants: consultantsRes.count ?? 0,
      clients: clientsRes.count ?? 0,
      openIncidents: incidentsRes.count ?? 0,
    });
    setLoadingStats(false);
  };

  const quickStats: QuickStat[] = [
    { label: 'Contact Messages', value: stats.contacts, color: 'text-teal-600' },
    { label: 'Active Consultants', value: stats.consultants, color: 'text-blue-600' },
    { label: 'Active Clients', value: stats.clients, color: 'text-green-600' },
    { label: 'Open Incidents', value: stats.openIncidents, color: stats.openIncidents > 0 ? 'text-red-600' : 'text-gray-700' },
  ];

  const securityCards: NavCard[] = [
    {
      title: 'Security Incidents',
      description: 'View and manage active security incidents, root causes, and MTTR metrics.',
      href: '/admin/security/incidents',
      icon: <ShieldAlert className="w-5 h-5" />,
      color: 'bg-red-50 text-red-600 border-red-100',
    },
    {
      title: 'Recovery & Snapshots',
      description: 'Browse audit snapshots and execute controlled restore workflows.',
      href: '/admin/security/recovery',
      icon: <RotateCcw className="w-5 h-5" />,
      color: 'bg-orange-50 text-orange-600 border-orange-100',
    },
    {
      title: 'Infrastructure Monitor',
      description: 'Monitor service health, deployment events, and suspicious login activity.',
      href: '/admin/security/infrastructure',
      icon: <Server className="w-5 h-5" />,
      color: 'bg-blue-50 text-blue-600 border-blue-100',
    },
    {
      title: '2FA Audit',
      description: 'Track 2FA adoption, failures, admin overrides, and lockout history.',
      href: '/admin/security/2fa-audit',
      icon: <ShieldCheck className="w-5 h-5" />,
      color: 'bg-teal-50 text-teal-600 border-teal-100',
    },
    {
      title: 'PayPal Audit',
      description: 'Environment comparison, webhook monitoring, and restore workflows.',
      href: '/admin/audit/paypal',
      icon: <CreditCard className="w-5 h-5" />,
      color: 'bg-green-50 text-green-600 border-green-100',
    },
    {
      title: 'Client Audit',
      description: 'Per-client activity timelines, integrity scores, and risk indicators.',
      href: '/admin/workday/clients/audit',
      icon: <Building2 className="w-5 h-5" />,
      color: 'bg-yellow-50 text-yellow-700 border-yellow-100',
    },
    {
      title: 'Consultant Audit',
      description: 'Consultant activity logs, application history, and moderation actions.',
      href: '/admin/workday/consultants/audit',
      icon: <UserCog className="w-5 h-5" />,
      color: 'bg-gray-50 text-gray-700 border-gray-200',
    },
  ];

  const managementCards: NavCard[] = [
    {
      title: 'Workday Consultants',
      description: 'Manage consultant profiles, status, and onboarding.',
      href: '/admin/workday-consultants',
      icon: <UserCheck className="w-5 h-5" />,
      color: 'bg-teal-50 text-teal-600 border-teal-100',
    },
    {
      title: 'Workday Clients',
      description: 'Manage client accounts, plans, and team members.',
      href: '/admin/workday-clients',
      icon: <Briefcase className="w-5 h-5" />,
      color: 'bg-blue-50 text-blue-600 border-blue-100',
    },
    {
      title: 'Job Postings',
      description: 'Review and manage all job postings and service requirements.',
      href: '/admin/job-postings',
      icon: <ClipboardList className="w-5 h-5" />,
      color: 'bg-green-50 text-green-600 border-green-100',
    },
    {
      title: 'Website Analytics',
      description: 'View login records, portal usage, and activity trends.',
      href: '/admin/analytics',
      icon: <BarChart3 className="w-5 h-5" />,
      color: 'bg-orange-50 text-orange-600 border-orange-100',
    },
    {
      title: 'Contact Messages',
      description: 'Review and respond to inbound contact form submissions.',
      href: '/admin/contacts',
      icon: <MessageSquare className="w-5 h-5" />,
      color: 'bg-gray-50 text-gray-700 border-gray-200',
    },
    {
      title: 'User Account Requests',
      description: 'Approve or reject new client team member invitation requests.',
      href: '/admin/user-requests',
      icon: <Users className="w-5 h-5" />,
      color: 'bg-yellow-50 text-yellow-700 border-yellow-100',
    },
    {
      title: 'Media Library',
      description: 'Upload and manage product images, logos, and media files.',
      href: '/admin/media',
      icon: <Image className="w-5 h-5" />,
      color: 'bg-pink-50 text-pink-600 border-pink-100',
    },
  ];

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-8">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-sm text-gray-500 mt-0.5">Manage your platform, security, and operations</p>
          </div>
          <button
            onClick={fetchStats}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <Activity className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {quickStats.map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              {loadingStats ? (
                <div className="h-8 w-16 bg-gray-100 animate-pulse rounded mb-1" />
              ) : (
                <div className={`text-3xl font-black tabular-nums ${stat.color}`}>{stat.value}</div>
              )}
              <div className="text-sm text-gray-500 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Open incidents banner */}
        {stats.openIncidents > 0 && (
          <Link
            to="/admin/security/incidents"
            className="flex items-center justify-between bg-red-50 border border-red-200 rounded-xl px-5 py-4 hover:bg-red-100 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-red-700">
                  {stats.openIncidents} open security incident{stats.openIncidents !== 1 ? 's' : ''} require attention
                </p>
                <p className="text-xs text-red-500 mt-0.5">Click to view and manage incidents</p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-red-400 group-hover:translate-x-1 transition-transform" />
          </Link>
        )}

        {/* Security & Audit */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <ShieldAlert className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Security & Audit</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {securityCards.map((card) => (
              <Link
                key={card.href}
                to={card.href}
                className="group bg-white rounded-xl border border-gray-200 shadow-sm p-5 hover:shadow-md hover:border-gray-300 transition-all"
              >
                <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border ${card.color} mb-3`}>
                  {card.icon}
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1 group-hover:text-teal-600 transition-colors">
                  {card.title}
                </h3>
                <p className="text-xs text-gray-500 leading-relaxed">{card.description}</p>
                <div className="flex items-center gap-1 mt-3 text-xs text-teal-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Open <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Platform Management */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Briefcase className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Platform Management</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {managementCards.map((card) => (
              <Link
                key={card.href}
                to={card.href}
                className="group bg-white rounded-xl border border-gray-200 shadow-sm p-5 hover:shadow-md hover:border-gray-300 transition-all"
              >
                <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border ${card.color} mb-3`}>
                  {card.icon}
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1 group-hover:text-teal-600 transition-colors">
                  {card.title}
                </h3>
                <p className="text-xs text-gray-500 leading-relaxed">{card.description}</p>
                <div className="flex items-center gap-1 mt-3 text-xs text-teal-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Open <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Content Management */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Image className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Content Management</h2>
          </div>
          <div className="space-y-6">
            <WorkdayCommunityManager />
            <ConsultantDocumentManager />
          </div>
        </section>

      </div>
    </AdminLayout>
  );
}
